import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {type Sx, Text} from '@mantine/core';
import {useSelector} from '@xstate/react';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';
import {useReactFlow} from 'reactflow';
import {NodeProps} from 'reactflow';
import {waitFor} from 'xstate';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {DEFAULT_FIT_VIEW_SETTINGS} from 'registry/entity-map/EntityMapConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import NoAccessChip from 'ui-library/chips/NoAccessChip';
import useHoverState from 'ui-library/hooks/useHoverState';
import {InvisibleNodeHandle} from 'ui-library/reactflow/handles/InvisibleNodeHandle';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {BenchlingNodeStatus} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeConstants';

import {EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode} from './__generated/EntityMap';
import {EntityMapMachineContext} from './EntityMapMachineProvider';
import EntityNodeSourceHandle from './EntityNodeSourceHandle';
export const HOVER_INFO_TOOLTIP_TEXT = {
  TOO_MANY_RELATIONSHIPS: 'This entity has too many relationships to load',
  ENTITY_MAP_LIMIT_REACHED: "Can't load more relationships, entity map limit reached",
};

/**
 * Returns the tooltip text to display for the node based on the node's state.
 *
 * TODO(BNCH-126917): Once we have implemented the entity map per node expandable relationships limit, we
 * should use that instead of the isError flag.
 */
const getTooltipText = ({
  isEntityMapSizeOverProductLimit,
  cannotBeExpandedDueToEdgeLimit,
}: {
  isEntityMapSizeOverProductLimit: boolean;
  cannotBeExpandedDueToEdgeLimit: boolean;
}): string | undefined => {
  if (isEntityMapSizeOverProductLimit) {
    return HOVER_INFO_TOOLTIP_TEXT.ENTITY_MAP_LIMIT_REACHED;
  }
  if (cannotBeExpandedDueToEdgeLimit) {
    return HOVER_INFO_TOOLTIP_TEXT.TOO_MANY_RELATIONSHIPS;
  }
  return undefined;
};

function EntityNodeHeader(props: EntityMapEntityNodeOrInaccessibleInfoNode): JSX.Element {
  if (isInaccessible(props)) {
    return <NoAccessChip size={ChipSize.SMALL} />;
  }
  return (
    <>
      <BioentityChip
        id={props.id}
        size={ChipSize.SMALL}
        className={css(ENTITY_NODE_HEADER_CHIP_STYLES.chip)}
      />
      {props.schemaName ? (
        <Text
          c="gray.6"
          fz="sm"
          sx={{
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {props.schemaName}
        </Text>
      ) : null}
    </>
  );
}

/**
 * Renders a node within the entity map for the given entity.
 */
export default function EntityNode(props: NodeProps<EntityMapEntityNodeOrInaccessibleInfoNode>): JSX.Element {
  const {fitView} = useReactFlow();
  // The node's hover state has a one-directional relationship with the handle's hover state. When the user
  // hovers over the node, we show the handle as hovered as well. However, when the user hovers over the
  // handle, we do not show the node as hovered.
  // TODO(BNCH-132192): We should decide on our strategy for state management. The EntityNodeMachine manages
  // most of the state for the node, and hover state should probably live in there too, as otherwise it will
  // be difficult to reason about where state lives.
  const [isNodeHovered, nodeHoverProps] = useHoverState();
  const [isHandleHovered, setIsHandleHovered] = useState<boolean>(false);

  const entityId = getAccessibleOrInaccessibleId(props.data);

  const isMainEntityMapNode = EntityMapMachineContext.useSelector(
    (snap) => snap.context.entityId === entityId
  );

  const isEntityMapSizeOverProductLimit = EntityMapMachineContext.useSelector(
    (snap) => snap.context.isEntityMapSizeOverProductLimit
  );

  const isFullyExpanded = EntityMapMachineContext.useSelector((snap) =>
    snap.context.fullyExpandedEntityIds.has(entityId)
  );

  // We use EntityMapMachineContext.useSelector here instead of the useEntityMapActorRef alias so that we're
  // able to effectively stub this in the unit tests
  const nodeActor = EntityMapMachineContext.useSelector((snap) =>
    snap.context.spawnedNodeMachines.get(entityId)
  );

  const entityMapActor = EntityMapMachineContext.useActorRef();

  const cannotBeExpandedDueToEdgeLimit = useSelector(
    nodeActor,
    (snap) => snap?.context.cannotBeExpandedDueToEdgeLimit ?? false
  );

  // isIdle determines if the node can load the extended graph
  const isIdle = useSelector(nodeActor, (snap) => snap?.matches('idle')) ?? false;
  const isLoading = useSelector(nodeActor, (snap) => snap?.matches('loading')) ?? false;
  const isError = useSelector(nodeActor, (snap) => snap?.matches('error')) ?? false;
  const isEntityMapIdle = useSelector(entityMapActor, (snap) => snap?.matches('idle')) ?? false;

  const status: BenchlingNodeStatus | undefined = isLoading ? 'pending' : isError ? 'error' : undefined;

  const isHandleExpandable = !isError && !isEntityMapSizeOverProductLimit && !cannotBeExpandedDueToEdgeLimit;
  const tooltipText = getTooltipText({
    isEntityMapSizeOverProductLimit,
    cannotBeExpandedDueToEdgeLimit,
  });

  const onClick = useCallback(async (): Promise<void> => {
    if (!isIdle || !isEntityMapIdle) {
      return;
    }

    nodeActor!.send({
      type: 'loadExtendedGraph',
    });

    // Ensure the node actor has finished loading the extended graph after expansion, and that the entity map
    // has finished laying out the graph before fitting the view focused on the expanded node.
    await waitFor(nodeActor!, (snap) => snap?.matches('done') || snap?.matches('error'));
    await waitFor(entityMapActor, (snap) => snap?.matches('idle'));
    fitView({nodes: [{id: entityId}], ...DEFAULT_FIT_VIEW_SETTINGS});
  }, [nodeActor, entityMapActor, isIdle, isEntityMapIdle, entityId, fitView]);

  return (
    <div
      {...getTestProps({
        component: 'EntityNode',
        element: 'root',
      })}
    >
      <BenchlingNode
        status={status}
        pb="0px"
        w="auto"
        maw="200px"
        sx={
          isMainEntityMapNode
            ? {
                border: `1px solid ${Color.BLUE_450}`,
              }
            : {}
        }
        {...nodeHoverProps}
        dataTestAttrs={{
          element: 'EntityNode',
        }}
      >
        <BenchlingNode.Header
          spacing="xs"
          {...(isMainEntityMapNode
            ? {
                bg: isError ? Color.RED_100 : Color.BLUE_50,
              }
            : {})}
          {...getTestProps({
            component: 'EntityNode',
            element: 'header',
          })}
        >
          <EntityNodeHeader {...props.data} />
        </BenchlingNode.Header>
      </BenchlingNode>
      <InvisibleNodeHandle nodeId={props.id} handleType="target" />
      <EntityNodeSourceHandle
        nodeId={props.id}
        isFullyExpanded={isFullyExpanded || isInaccessible(props.data)}
        isExpandable={isHandleExpandable}
        onClick={onClick}
        tooltipText={tooltipText}
        isHovered={isNodeHovered || isHandleHovered}
        setIsHovered={setIsHandleHovered}
      />
    </div>
  );
}

const ENTITY_NODE_HEADER_CHIP_STYLES = StyleSheet.create({
  chip: {
    marginRight: '0px',
  },
});
